import { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";

export function Contact({ email, phoneNumber, saveContactData }) {
  const [isEditing, setIsEditing] = useState(false);
  const [emailValue, setEmailValue] = useState("");
  const [phoneValue, setPhoneValue] = useState("");

  useEffect(() => {
    setEmailValue(email);
    setPhoneValue(phoneNumber);
  }, [email, phoneNumber]);

  const handleSave = () => {
    saveContactData({
      email: emailValue,
      phoneNumber: phoneValue,
    });
    setIsEditing(false);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEmailValue(email);       // Reset values
    setPhoneValue(phoneNumber);
  };

  return (
    <div className={`bg-white rounded-xl p-6 relative transition-all duration-300 ${isEditing ? "glow-burst glow-hold" : "shadow"}`}>
      <div className="flex justify-between items-center mb-2">
        <h2 className="text-lg font-semibold mb-2">{!isEditing ? 'Contact' : 'Edit Contact'}</h2>

        {!isEditing ? (
          <button
            className="absolute top-5 right-6 text-gray-500 hover:text-black hover:bg-gray-200 focus:outline-none py-1 px-2 rounded-full transition"
            onClick={() => setIsEditing(true)}
            title="Edit Contact"
          >
            <i className="fa-solid fa-pencil" />
          </button>
        ) : (
          <div className="absolute top-5 right-6 flex gap-2">
            <button
              className="text-gray-500 hover:text-black focus:outline-none py-1 px-2.5 rounded-full hover:bg-gray-200 transition"
              onClick={handleSave}
              title="Save"
            >
              <i className="fa-solid fa-floppy-disk" />
            </button>
            <button
              className="text-gray-500 hover:text-black focus:outline-none py-1 px-2.5 rounded-full hover:bg-gray-200 transition"
              onClick={handleCancel}
              title="Cancel"
            >
              <i className="fa-solid fa-xmark" />
            </button>
          </div>
        )}
      </div>

      <AnimatePresence mode="wait">
        {!isEditing ? (
          <motion.div
            key="contact-view"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="flex flex-col gap-1 text-sm text-gray-700">
              <p>📧 {email}</p>
              <p>📞 {phoneNumber}</p>
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="contact-edit"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="flex flex-col gap-2">
              <input
                type="email"
                className="w-full border-b border-gray-300 hover:border-[#365561] focus:border-[#365561] focus:outline-none text-sm pb-0.1"
                placeholder="Email Address"
                value={emailValue}
                onChange={(e) => setEmailValue(e.target.value)}
              />
              <input
                type="text"
                className="w-full border-b border-gray-300 hover:border-[#365561] focus:border-[#365561] focus:outline-none text-sm pb-0.1"
                placeholder="Phone Number"
                value={phoneValue}
                onChange={(e) => setPhoneValue(e.target.value)}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Glowing box-shadow animation */}
      <style>
        {`
          @keyframes glowBurst {
            0% {
              box-shadow: 0 0 0px 0px rgba(54, 85, 97, 0);
            }
            30% {
              box-shadow: 0 0 20px 6px rgba(54, 85, 97, 0.6);
            }
            100% {
              box-shadow: 0 0 15px 3px rgba(54, 85, 97, 0.6);
            }
          }

          .glow-burst {
            animation: glowBurst 0.8s ease-out forwards;
            border-radius: 0.75rem;
          }

          .glow-hold {
            box-shadow: 0 0 20px 6px rgba(54, 85, 97, 0.6);
            border-radius: 0.75rem;
          }
        `}
      </style>
    </div>
  );
}
